'use client';

import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import VideoContainer from './VideoContainer';

interface ClientMarkdownProps {
    children: string;
}

export default function ClientMarkdown({ children }: ClientMarkdownProps) {
    // Store iframe content for later use
    const iframes: string[] = [];

    // Preprocess the markdown to handle special cases
    const preprocessMarkdown = (content: string): string => {
        let processedContent = content
            // Convert standalone double dashes to proper markdown horizontal rules
            .replace(/^--$/gm, '---')
            // Handle lines that start with double dashes
            .replace(/^--\s*$/gm, '---');

        // Extract and replace iframe HTML with placeholders
        // Handle div-wrapped iframes first (more specific pattern)
        processedContent = processedContent.replace(
            /<div[^>]*>[\s\S]*?<iframe[\s\S]*?<\/iframe>[\s\S]*?<\/div>/gi,
            (match) => {
                const index = iframes.length;
                iframes.push(match);
                return `\n\nIFRAME_PLACEHOLDER_${index}\n\n`;
            }
        );

        // Then handle standalone iframes
        processedContent = processedContent.replace(/<iframe[\s\S]*?<\/iframe>/gi, (match) => {
            const index = iframes.length;
            iframes.push(match);
            return `\n\nIFRAME_PLACEHOLDER_${index}\n\n`;
        });

        return processedContent;
    };

    const customComponents: Components = {
        // Handle horizontal rules
        hr: () => <hr className="my-6 border-haug-choco-30 border-t" />,

        // Handle text nodes that might contain iframe placeholders
        text: ({ children }) => {
            const value = typeof children === 'string' ? children : String(children);

            // Check if this text contains an iframe placeholder
            const iframePlaceholderMatch = value.match(/^IFRAME_PLACEHOLDER_(\d+)$/);

            if (iframePlaceholderMatch) {
                const iframeIndex = Number.parseInt(iframePlaceholderMatch[1], 10);
                const iframeHtml = iframes[iframeIndex];

                if (iframeHtml) {
                    return (
                        <div className="my-6">
                            <VideoContainer iframeHtml={iframeHtml} />
                        </div>
                    );
                }

                // If we don't have the iframe content, render a message
                console.warn(`No iframe content found for placeholder ${iframeIndex}`);
                return <span>Video unavailable</span>;
            }

            // Regular text rendering
            return <>{value}</>;
        },

        // Handle iframe placeholders in paragraphs as backup
        p: ({ children }) => {
            // Check if this paragraph contains an iframe placeholder
            const childrenArray = Array.isArray(children) ? children : [children];
            const text = childrenArray.join('').trim();

            const iframePlaceholderMatch = text.match(/^IFRAME_PLACEHOLDER_(\d+)$/);

            if (iframePlaceholderMatch) {
                const iframeIndex = Number.parseInt(iframePlaceholderMatch[1], 10);
                const iframeHtml = iframes[iframeIndex];

                if (iframeHtml) {
                    return (
                        <div className="my-6">
                            <VideoContainer iframeHtml={iframeHtml} />
                        </div>
                    );
                }

                // If we don't have the iframe content, render a message or nothing
                console.warn(`No iframe content found for placeholder ${iframeIndex}`);
                return null;
            }

            // Regular paragraph rendering
            return (
                <p className="mb-4">
                    {children}
                </p>
            );
        },
    };

    const processedContent = preprocessMarkdown(children);

    return <ReactMarkdown components={customComponents}>{processedContent}</ReactMarkdown>;
}
